import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import useActionSyncUser from "../../../../../../hooks/useSyncUser";
import { Error } from "../../../../../../models/error";
import { Filters, Searches, Sort, Sorts } from "../../../../../../models/shipeezi.table";
import { Roles, User } from "../../../../../../models/user";
import RoleService from "../../../../../../services/role.service";
import UserService from "../../../../../../services/user.service";
import { checkError } from "../../../../../../util/checkError";
import { ErrorCodeEnum } from "Util/enums/errorCode";
import { BulkAssignPermission, ResourceFunction } from "Models/resources";
import { Status } from "Util/enums";

const useActions = () => {
  const { currentUser, statusAccessRuleGrid, setStatusAccessRuleGrid } = useAppState();
  const { t: translate } = useTranslation();

  const [roles, setRoles] = useState<Roles[]>([]);
  const [permissions, setPermissions] = useState<ResourceFunction[]>([]);
  const [roleResources, setRoleResources] = useState<any[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [isMutatingAccessRule, setIsMutatingAccessRule] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const [errorSpecialCharacter, setErrorSpecialCharacter] = useState<boolean>(false);
  const [errorUniqueAccessRuleName, setErrorUniqueAccessRuleName] = useState<boolean>(false);
  const { mutate: syncUser } = useActionSyncUser();
  const showAllStatusCode = "showAll";

  const handleSyncUser = (roleId: number) => {
    if (roleId && roleId === currentUser?.roleId) {
      syncUser();
    }
  };

  const { refetch, isFetching } = useQuery(
    ["roles.list", page, pageSize, filters, searches, sorting, statusAccessRuleGrid],
    () =>
      RoleService.getAllIncludeGroups({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "resources" }]),
        sortBy: sorting
          ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
          : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        status: statusAccessRuleGrid ? statusAccessRuleGrid : [Status.ACTIVE, Status.INACTIVE],
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setRoles(response.data);
      },
    }
  );

  const { refetch: refetchRolesTotal } = useQuery(
    ["roles.total", filters, searches],
    () =>
      RoleService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: updateRole, isLoading: isUpdatingRole } = useMutation(
    ["role.update.status"],
    ({ id, role }: { id: number; role: Roles }) => RoleService.update(id, role),
    {
      onSuccess: (_, variables) => {
        setHasErrors(false);
        refetch();
        refetchRolesTotal();
        handleSyncUser(variables?.id);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) checkError(error, translate("anErrorOccurredTryAgain"));
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateUser, isLoading: isUpdatingUser } = useMutation(
    ["user.update.status"],
    ({ id, user }: { id: number; user: User }) => UserService.update(id, user),
    {
      onSuccess: () => {
        setHasErrors(false);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) checkError(error, translate("anErrorOccurredTryAgain"));
        setHasErrors(true);
      },
    }
  );

  const { isLoading: isCreating, mutate: createRole } = useMutation(
    ["role.create"],
    (role: Roles) => {
      setIsMutatingAccessRule(true);
      if (role?.resources) setRoleResources(role?.resources);
      return RoleService.create(role)
    },
    {
      onSuccess: (response) => {
        setIsMutatingAccessRule(false);
        setErrorSpecialCharacter(false);
        setErrorUniqueAccessRuleName(false);
        setHasErrors(false);
        refetch();
        refetchRolesTotal();
        bulkAssign({
          roleId: response?.data?.id,
          assignedBy: response?.data?.name,
        });
        NotificationToast.success(translate("newAccessRuleAdded", { accessRuleName: response?.data?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingAccessRule(false);
        if (error && error.response) {
          if (error.response.data?.errorCode === ErrorCodeEnum.NOT_SPECIAL_ROLE_NAME) {
            setErrorSpecialCharacter(true);
          } else if (error.response.data?.errorCode === ErrorCodeEnum.UNIQUE_ROLE_NAME) {
            setErrorUniqueAccessRuleName(true);
          } else {
            NotificationToast.error(translate("anErrorOccurredTryAgain"));
          }
        }
        setHasErrors(true);
      },
    }
  );

  const { isLoading: isDuplicating, mutate: duplicateRole } = useMutation(
    ["role.create"],
    (id: number) => {
      setIsMutatingAccessRule(true);
      return RoleService.duplicate(id)
    },
    {
      onSuccess: (response) => {
        setIsMutatingAccessRule(false);
        setErrorSpecialCharacter(false);
        setHasErrors(false);
        refetch();
        refetchRolesTotal();
        NotificationToast.success(translate("newAccessRuleAddedAndSetAsInactive", { accessRuleName: response?.data?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingAccessRule(false);
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { isLoading: isLoadingUpdatingAccessRule, mutate: updateAccessRule } = useMutation(
    ["role.update"],
    ({ id, role }: { id: number; role: any }) => {
      setIsMutatingAccessRule(true);
      if (role?.resources) setRoleResources(role?.resources);
      return RoleService.update(id, role);
    },
    {
      onSuccess: (_, variables) => {
        setIsMutatingAccessRule(false);
        setErrorSpecialCharacter(false);
        setErrorUniqueAccessRuleName(false);
        setHasErrors(false);
        refetch();
        refetchRolesTotal();
        bulkAssign({
          roleId: variables?.role?.id,
          assignedBy: variables?.role?.name,
        });
        NotificationToast.success(translate("accessRuleUpdatedSuccessfully", { accessRuleName: variables?.role?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingAccessRule(false);
        if (error && error.response) {
          if (error.response.data?.errorCode === ErrorCodeEnum.NOT_SPECIAL_ROLE_NAME) {
            setErrorSpecialCharacter(true);
          } else if (error.response.data?.errorCode === ErrorCodeEnum.UNIQUE_ROLE_NAME) {
            setErrorUniqueAccessRuleName(true);
          } else {
            NotificationToast.error(translate("anErrorOccurredTryAgain"));
          }
        }
        setHasErrors(true);
      },
    }
  );

  const { isLoading: isDeletingAccessRule, mutate: deleteAccessRule } = useMutation(
    ["role.delete"],
    ({ id, role }: { id: number; role: any }) => {
      setIsMutatingAccessRule(true);
      return RoleService.delete(id)
    },
    {
      onSuccess: (_, variables) => {
        setIsMutatingAccessRule(false);
        setHasErrors(false);
        refetch();
        refetchRolesTotal();
        NotificationToast.success(translate("accessRuleDeletedSuccessfully", { accessRuleName: variables?.role?.name }));
      },
      onError: (error: AxiosError<Error>) => {
        setIsMutatingAccessRule(false);
        if (error && error.response) NotificationToast.error(translate("anErrorOccurredTryAgain"));
        setHasErrors(true);
      },
    }
  )

  const { mutate: bulkAssign } = useMutation(
    ["permissions.bulk-assign"],
    (bulkInfo: {roleId: number, assignedBy: string}) => {
      let formatRoleResources: BulkAssignPermission[] = [];
      let formaPermissions: BulkAssignPermission[] = [];
      if (roleResources && roleResources?.length > 0) {
        formatRoleResources = roleResources?.map(roleResource => {
          return { 
            objectId: roleResource?.id, resourceId: roleResource?.id,
            objectType: "resource", objectData: roleResource?.objectData ?? "",
            canView: roleResource?.view, canEdit: roleResource?.edit 
          }
        })
      }

      if (permissions && permissions?.length > 0) {
        formaPermissions = permissions?.map(permission => {
          return { 
            objectId: permission?.id, resourceId: permission?.resourceId, 
            objectType: permission?.objectType,
            objectData: permission?.objectData ?? "",
            canView: true, canEdit: true
          }
        })
      }

      const payload = {
        roleId: bulkInfo?.roleId,
        permissions: formatRoleResources?.concat(formaPermissions),
        assignedBy: bulkInfo?.assignedBy
      }

      return RoleService.bulkAssign(payload)
    },
    {
      onSuccess: (response) => {
        setHasErrors(false);
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(translate("failedToAssignPermissionsToAccessRulePleaseTryAgain"));
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    // setFilters(value);
    setStatusAccessRuleGrid(value?.status);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    roles,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading: isFetching || isUpdatingUser || isUpdatingRole || isCreating || isDuplicating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    updateRole,
    updateUser,
    createRole,
    duplicateRole,
    refetch,
    refetchRolesTotal,
    showAllStatusCode,
    errorSpecialCharacter,
    setErrorSpecialCharacter,
    isCreating,
    isDuplicating,
    isLoadingUpdatingAccessRule,
    isDeletingAccessRule,
    updateAccessRule,
    deleteAccessRule,
    setPermissions,
    isMutatingAccessRule,
    errorUniqueAccessRuleName, 
    setErrorUniqueAccessRuleName
  };
};

export default useActions;
